import { type FlightStatus, type FlightSummary, MIN_CONNECTION_MINUTES, type TravelDirection, formatFlightDesignator } from "@seagames/contracts";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatClockIn, formatDateTime, formatDayShort, wallTimeIn, zoneAbbreviation } from "../../lib/format/datetime";
import { FlightCardCompact } from "../travel/flight-card/flight-card";
import { cardFromFlight } from "../travel/flight-card/model";
import { ModeIcon } from "./icons";
import { type TravelLegDraft, changedTime, expectedOf, terminalLabel, viaAirports } from "./model";

/**
 * How a flight / a leg reads anywhere in the app: the status chip, the
 * "MH 803 · Manila (MNL) → KUL T2" line, and the time in the games' zone
 * ("Mon 3 Nov · 1:00PM MYT") with the scheduled time struck through when the
 * expected one moved (her `.del` timeline rows). The viewer's own reading is
 * the tooltip (`formatDateTime`), per the time-zone rule.
 */

const TONE: Record<FlightStatus, string> = {
	SCHEDULED: "bg-gms-navy-soft text-gms-navy",
	DELAYED: "bg-gms-warn-soft text-gms-warn",
	DEPARTED: "bg-gms-navy text-white",
	LANDED: "bg-gms-ok-soft text-gms-ok",
	CANCELLED: "bg-gms-bad-soft text-gms-bad",
	DIVERTED: "bg-gms-bad-soft text-gms-bad",
	UNKNOWN: "bg-gms-soft-2 text-gms-ink-2",
};

/** Her `.dchip`: a dot and the status, coloured by what it means for the welcome desk. */
export function FlightStatusChip({ status, className = "" }: { status: FlightStatus; className?: string }) {
	const { t } = useTranslation("travel");
	return (
		<span
			title={t(`legForm.statusHint.${status}`)}
			className={`inline-flex h-[1.375rem] shrink-0 items-center gap-[0.3125rem] rounded-selector px-[0.5rem] text-[0.6875rem] font-semibold whitespace-nowrap ${TONE[status]} ${className}`}
		>
			<span aria-hidden className="size-[0.375rem] rounded-full bg-current" />
			{t(`legForm.status.${status}`)}
		</span>
	);
}

/** "MH 803 · Manila (MNL) → KUL T2" for an arrival; "MH 804 · KUL → MNL" for a departure. */
export function FlightRoute({ flight, className = "" }: { flight: FlightSummary; className?: string }) {
	const { t } = useTranslation("travel");
	const from = flight.origin_name && flight.origin_iata ? t("legForm.flight.place", { name: flight.origin_name, code: flight.origin_iata }) : (flight.origin_iata ?? flight.origin_name ?? "—");
	const to = flight.destination_iata ? (flight.destination_terminal ? t("legForm.flight.terminal", { code: flight.destination_iata, terminal: terminalLabel(flight.destination_terminal) }) : flight.destination_iata) : "—";
	return (
		<span className={`inline-flex min-w-0 flex-wrap items-baseline gap-x-[0.375rem] ${className}`}>
			<b className="font-semibold tracking-[0.02em] text-gms-navy-ink">{formatFlightDesignator(flight.carrier_code, flight.flight_number)}</b>
			<span className="text-gms-ink-2">{t("legForm.flight.route", { from, to })}</span>
		</span>
	);
}

/** Amber "Connection at risk" (`connection_at_risk`): a change under MIN_CONNECTION_MINUTES, or a connecting flight cancelled / diverted. */
export function ConnectionRiskChip({ className = "" }: { className?: string }) {
	const { t } = useTranslation("travel");
	return (
		<span
			title={t("legForm.transit.atRiskHint", { count: MIN_CONNECTION_MINUTES })}
			className={`inline-flex h-[1.375rem] shrink-0 items-center gap-[0.3125rem] rounded-selector bg-gms-warn-soft px-[0.5rem] text-[0.6875rem] font-semibold whitespace-nowrap text-gms-warn ${className}`}
		>
			<span aria-hidden className="size-[0.375rem] rounded-full bg-current" />
			{t("legForm.transit.atRisk")}
		</span>
	);
}

/**
 * "via BKI" / "via BKI, SIN" next to a transit leg's flight; the connecting
 * flights are the tooltip. Nothing for a direct leg.
 */
export function ConnectionVia({ direction, flight, connections, className = "" }: { direction: TravelDirection; flight: FlightSummary | null; connections: readonly FlightSummary[]; className?: string }) {
	const { t } = useTranslation("travel");
	const codes = viaAirports(direction, flight, connections);
	if (codes.length === 0) return null;
	const title = connections.map((f) => `${formatFlightDesignator(f.carrier_code, f.flight_number)} ${t("legForm.flight.route", { from: f.origin_iata ?? "—", to: f.destination_iata ?? "—" })}`).join("\n");
	return (
		<span title={title} className={`whitespace-nowrap text-gms-ink-2 ${className}`}>
			{t("legForm.transit.via", { airports: codes.join(", ") })}
		</span>
	);
}

/**
 * A leg's time in the games' zone. `scheduled` struck through and `expected`
 * after it when they differ by a minute or more; the day comes from the
 * expected instant (a delay past midnight moves the day too), or from
 * `travelDate` when no time is known yet ("Mon 3 Nov · time not known yet").
 */
export function LegTime({
	direction,
	travelDate,
	scheduled,
	expected,
	timeZone,
	className = "",
}: {
	direction: TravelDirection;
	travelDate: string;
	scheduled: string | null;
	expected: string | null;
	timeZone: string;
	className?: string;
}) {
	const { t } = useTranslation("travel");
	const zone = zoneAbbreviation(timeZone);
	const shown = expected ?? scheduled;
	const day = formatDayShort(wallTimeIn(shown, timeZone)?.date ?? travelDate);
	if (!shown)
		return (
			<span className={`inline-flex flex-wrap items-baseline gap-x-[0.375rem] ${className}`}>
				<span className="font-medium text-gms-navy-ink">{day}</span>
				<span className="text-gms-ink-3">{t("legForm.time.unknown")}</span>
			</span>
		);
	const moved = changedTime(scheduled, expected);
	const label = moved
		? t(direction === "ARRIVAL" ? "legForm.time.movedArrival" : "legForm.time.movedDeparture", {
				scheduled: formatClockIn(scheduled, timeZone),
				expected: formatClockIn(expected, timeZone),
				zone,
			})
		: undefined;
	return (
		<span className={`inline-flex flex-wrap items-baseline gap-x-[0.375rem] tabular-nums ${className}`} aria-label={label ? `${day}, ${label}` : undefined}>
			<span className="font-medium text-gms-navy-ink">{day}</span>
			{moved && scheduled && (
				<del className="text-gms-ink-3 decoration-gms-ink-3/70" title={formatDateTime(scheduled)}>
					{formatClockIn(scheduled, timeZone)}
				</del>
			)}
			<time dateTime={shown} title={formatDateTime(shown)} className={`font-semibold ${moved ? "text-gms-warn" : "text-gms-navy-ink"}`}>
				{t("legForm.time.at", { time: formatClockIn(shown, timeZone), zone })}
			</time>
		</span>
	);
}

/**
 * A leg on one line, for read-only summaries (the Register flow's Review):
 * mode glyph, the flight or service, the entry point, the time. Values only,
 * separated by her middle dots (data, not translated fragments).
 */
export function LegSummary({ direction, leg, timeZone }: { direction: TravelDirection; leg: TravelLegDraft; timeZone: string }) {
	const { t } = useTranslation("travel");
	const { scheduled, expected } = expectedOf(direction, leg);
	const service = leg.flight ? formatFlightDesignator(leg.flight.carrier_code, leg.flight.flight_number) : leg.service_no.trim();
	const via = leg.mode === "AIR" ? viaAirports(direction, leg.flight, leg.connections.flatMap((c) => (c.flight ? [c.flight] : []))) : [];
	const parts = [service, via.length ? t("legForm.transit.via", { airports: via.join(", ") }) : null, leg.entry_point?.name].filter((p): p is string => !!p);
	return (
		<span className="inline-flex min-w-0 flex-wrap items-center gap-x-[0.375rem] gap-y-[0.125rem]">
			{leg.mode && <ModeIcon mode={leg.mode} className="size-[1rem] shrink-0 text-gms-ink-3" />}
			{parts.map((p) => (
				<span key={p} className="after:ml-[0.375rem] after:text-gms-ink-3 after:content-['·']">
					{p}
				</span>
			))}
			{leg.travel_date ? <LegTime direction={direction} travelDate={leg.travel_date} scheduled={scheduled} expected={expected} timeZone={timeZone} /> : null}
		</span>
	);
}

/**
 * A found flight as a card (user, 2026-10-15) — the flight card's `compact`
 * size (approved 2026-11-03): the airline row (logo, flight, its day, the
 * status unless on time, `action` top right), then the route end to end,
 * each end in its AIRPORT's own time with the zone written beside it.
 * `games` marks the main flight, the one at a Games airport. `timeZone`
 * (the Games') is kept for callers; a flight's ends never read it.
 */
export function FlightCard({
	direction,
	flight,
	entryPoint = null,
	games = false,
	action,
}: {
	/** The leg's direction; a connection is drawn the same either way. */
	direction: TravelDirection;
	flight: FlightSummary;
	entryPoint?: TravelLegDraft["entry_point"];
	timeZone: string;
	games?: boolean;
	action?: ReactNode;
}) {
	const { t } = useTranslation("travel");
	return (
		<FlightCardCompact
			model={cardFromFlight(direction, flight, entryPoint)}
			action={action}
			badge={
				games ? (
					<span className="inline-flex h-[1.375rem] items-center rounded-selector bg-gms-ok-soft px-[0.5rem] text-[0.6875rem] font-semibold whitespace-nowrap text-gms-ok">
						{direction === "ARRIVAL" ? t("legForm.flight.gamesAirportArrival") : t("legForm.flight.gamesAirportDeparture")}
					</span>
				) : undefined
			}
		/>
	);
}
